import React, { useState } from "react";
import { 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Stack,
  Grid,
  Paper,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  alpha,
  Divider,
  Chip,
  useTheme
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SendIcon from '@mui/icons-material/Send';
import PublicIcon from '@mui/icons-material/Public';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BusinessIcon from '@mui/icons-material/Business';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import VerifiedIcon from '@mui/icons-material/Verified';
import SecurityIcon from '@mui/icons-material/Security';

// Styled Components - Matching site aesthetic
const OfficeCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: '#FFFFFF',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
  transition: 'all 0.3s ease',
  height: '100%',
  borderRadius: 2,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '4px',
    height: '100%',
    backgroundColor: theme.palette.primary.main,
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  '&:hover': {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
    transform: 'translateY(-4px)',
    '&::before': {
      opacity: 1,
    },
  },
}));

const ContactCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(3),
  backgroundColor: '#FFFFFF',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: 'all 0.3s ease',
  borderRadius: 2,
  '&:hover': {
    borderColor: theme.palette.primary.main,
    transform: 'translateX(8px)',
    boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
  },
}));

const StatCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  backgroundColor: '#FFFFFF',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  borderRadius: 2,
  textAlign: 'center',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    transform: 'translateY(-2px)',
    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.1)}`,
  },
}));

const Contact = () => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    inquiryType: ''
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Prepare email content
    const subject = `Contact Form: ${formData.subject}`;
    const body = `
      CONTACT FORM SUBMISSION
      
      From: ${formData.name}
      Email: ${formData.email}
      Inquiry Type: ${formData.inquiryType}
      
      Message:
      ${formData.message}
    `;

    // Create mailto link
    const mailtoLink = `mailto:concierge@diamondcoin.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    // Open default email client
    window.location.href = mailtoLink;
    
    setSnackbar({
      open: true,
      message: 'Thank you for your message. Our concierge will respond within 24 hours.',
      severity: 'success'
    });
    setFormData({
      name: '',
      email: '',
      subject: '',
      message: '',
      inquiryType: ''
    });
  };

  const offices = [
    {
      country: "CANADA",
      flag: "🇨🇦",
      address: [
        "158 Barkley Terrace",
        "Victoria, British Columbia",
        "Canada, V8S-2J6"
      ],
      phone: "+1-250-883-5000",
      email: "canada@diamondcoin.com",
      hours: "Mon-Fri: 9am - 6pm PST",
      timezone: "Pacific Time"
    },
    {
      country: "NEW ZEALAND",
      flag: "🇳🇿",
      address: [
        "14, 19 Lantana Avenue",
        "Auckland",
        "New Zealand, 0630"
      ],
      phone: "+64 21 1983352",
      email: "nz@diamondcoin.com",
      hours: "Mon-Fri: 9am - 6pm NZST",
      timezone: "New Zealand Time"
    }
  ];

  const contactInfo = [
    { 
      icon: <EmailIcon />, 
      title: "Email", 
      primary: "concierge@diamondcoin.com",
      secondary: "Response within 24 hours"
    },
    { 
      icon: <AccessTimeIcon />, 
      title: "Global Hours", 
      primary: "Monday - Friday",
      secondary: "9:00 AM - 6:00 PM Local"
    },
    { 
      icon: <SupportAgentIcon />, 
      title: "Live Support", 
      primary: "Premium Members",
      secondary: "24/7 Priority Access"
    },
  ];

  const stats = [
    { value: "24h", label: "Response Time" },
    { value: "100%", label: "Confidential" },
    { value: "2", label: "Global Offices" },
    { value: "5000+", label: "Clients Served" },
  ];

  return (
    <Box sx={{ 
      py: { xs: 6, md: 10 }, 
      backgroundColor: '#F5F5F5', 
      minHeight: '100vh' 
    }}>
      <Container maxWidth="lg">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <Typography 
            variant="h2" 
            align="center" 
            gutterBottom 
            sx={{ 
              color: '#2D2D2D',
              fontWeight: 700,
              letterSpacing: '-0.02em',
              mb: 2,
              fontSize: { xs: '2.5rem', md: '3.5rem' }
            }}
          >
            Private
            <Box component="span" sx={{ 
              color: 'primary.main', 
              mx: 2,
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 8,
                left: 0,
                width: '100%',
                height: '8px',
                backgroundColor: alpha('#C6A75E', 0.2),
                zIndex: -1,
              }
            }}>
              Concierge
            </Box>
          </Typography>
          <Typography 
            variant="body1" 
            align="center" 
            sx={{ 
              color: '#616161', 
              maxWidth: '700px', 
              mx: 'auto', 
              mb: 6,
              fontSize: '1.2rem',
              lineHeight: 1.7
            }}
          >
            Our dedicated team is available to assist with acquisitions, valuations, 
            or any numismatic inquiry. Experience white-glove service for discerning collectors.
          </Typography>
        </motion.div>

        {/* Stats Section */}
        <Grid container spacing={3} sx={{ mb: 8 }}>
          {stats.map((stat, index) => (
            <Grid item xs={6} md={3} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <StatCard>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      color: 'primary.main', 
                      fontWeight: 700,
                      fontSize: { xs: '1.8rem', md: '2.2rem' },
                      lineHeight: 1.2,
                      mb: 0.5
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#616161', fontWeight: 500 }}>
                    {stat.label}
                  </Typography>
                </StatCard>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={5}>
          {/* Left Column - Contact Information */}
          <Grid item xs={12} md={5}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Typography 
                variant="h4" 
                gutterBottom 
                sx={{ 
                  mb: 4, 
                  color: '#2D2D2D',
                  fontWeight: 700,
                  fontSize: '2rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5
                }}
              >
                <BusinessIcon sx={{ color: 'primary.main', fontSize: 32 }} />
                Direct Access
              </Typography>

              <Stack spacing={3} sx={{ mb: 4 }}>
                {contactInfo.map((info, index) => (
                  <ContactCard key={index}>
                    <Box sx={{ 
                      color: 'primary.main',
                      backgroundColor: alpha('#C6A75E', 0.1),
                      width: 60,
                      height: 60,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: 'primary.main',
                        color: '#FFFFFF',
                        transform: 'scale(1.1)',
                      },
                    }}>
                      {React.cloneElement(info.icon, { sx: { fontSize: 28 } })}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" sx={{ color: '#9E9E9E', fontSize: '0.7rem', letterSpacing: '0.1em', mb: 0.5 }}>
                        {info.title}
                      </Typography>
                      <Typography variant="h6" sx={{ color: '#2D2D2D', fontSize: '1.1rem', fontWeight: 600, mb: 0.5 }}>
                        {info.primary}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#9E9E9E', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <VerifiedIcon sx={{ color: 'primary.main', fontSize: 12 }} />
                        {info.secondary}
                      </Typography>
                    </Box>
                  </ContactCard>
                ))}
              </Stack>

              {/* Trust Badges */}
              <Paper
                sx={{
                  p: 3,
                  backgroundColor: alpha('#C6A75E', 0.05),
                  border: '1px solid',
                  borderColor: alpha('#C6A75E', 0.2),
                  borderRadius: 2,
                }}
              >
                <Typography variant="subtitle2" sx={{ color: '#2D2D2D', fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SecurityIcon sx={{ color: 'primary.main', fontSize: 18 }} />
                  Our Commitment
                </Typography>
                <Grid container spacing={2}>
                  {[
                    "24h Response",
                    "Strict Confidentiality",
                    "Secure Transactions",
                    "Expert Consultation"
                  ].map((item, i) => (
                    <Grid item xs={6} key={i}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 6, height: 6, backgroundColor: 'primary.main', borderRadius: '50%' }} />
                        <Typography variant="caption" sx={{ color: '#616161' }}>
                          {item}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </motion.div>
          </Grid>

          {/* Right Column - Contact Form */}
          <Grid item xs={12} md={7}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Paper
                component="form"
                onSubmit={handleSubmit}
                elevation={0}
                sx={{
                  p: { xs: 4, md: 5 },
                  backgroundColor: '#FFFFFF',
                  border: '1px solid',
                  borderColor: alpha('#C6A75E', 0.15),
                  boxShadow: `0 20px 40px ${alpha('#000000', 0.08)}`,
                  borderRadius: 2,
                }}
              >
                <Typography 
                  variant="h5" 
                  gutterBottom 
                  sx={{ 
                    mb: 4, 
                    color: '#2D2D2D',
                    fontWeight: 700,
                    fontSize: '1.8rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <SendIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                  Send a Message
                </Typography>

                <Stack spacing={3.5}>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Full Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        variant="outlined"
                        size="medium"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'primary.main',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'primary.main',
                              borderWidth: '2px',
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email Address"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        variant="outlined"
                        size="medium"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'primary.main',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'primary.main',
                              borderWidth: '2px',
                            },
                          },
                        }}
                      />
                    </Grid>
                  </Grid>

                  <FormControl fullWidth>
                    <InputLabel sx={{ color: '#616161' }}>Inquiry Type</InputLabel>
                    <Select
                      name="inquiryType"
                      value={formData.inquiryType}
                      onChange={handleChange}
                      label="Inquiry Type"
                      required
                      sx={{
                        borderRadius: 1.5,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main',
                          borderWidth: '2px',
                        },
                      }}
                    >
                      <MenuItem value="acquisition">💰 Acquisition Inquiry</MenuItem>
                      <MenuItem value="valuation">📋 Valuation Request</MenuItem>
                      <MenuItem value="authentication">🔍 Authentication Services</MenuItem>
                      <MenuItem value="selling">💎 Selling Coins</MenuItem>
                      <MenuItem value="general">📝 General Inquiry</MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    label="Subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main',
                          borderWidth: '2px',
                        },
                      },
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Message"
                    name="message"
                    multiline
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Please provide details about your inquiry..."
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main',
                          borderWidth: '2px',
                        },
                      },
                    }}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForwardIcon />}
                    sx={{ 
                      py: 2,
                      background: 'linear-gradient(135deg, #C6A75E 0%, #9E823C 100%)',
                      color: '#000000',
                      fontWeight: 600,
                      fontSize: '1rem',
                      letterSpacing: '0.05em',
                      borderRadius: 1.5,
                      '&:hover': {
                        background: 'linear-gradient(135deg, #D4B77A 0%, #C6A75E 100%)',
                        transform: 'translateY(-2px)',
                        boxShadow: `0 12px 28px ${alpha('#C6A75E', 0.4)}`,
                      },
                    }}
                  >
                    Send Message
                  </Button>

                  <Typography variant="caption" sx={{ color: '#9E9E9E', textAlign: 'center', mt: 1 }}>
                    All inquiries are handled with strict confidentiality.
                    Our team typically responds within 24 hours.
                  </Typography>
                </Stack>
              </Paper>
            </motion.div>
          </Grid>
        </Grid>

        {/* Global Offices Section */}
        <Box sx={{ mt: 10 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Box sx={{ mb: 5, textAlign: 'center' }}>
              <Typography 
                variant="h3" 
                sx={{ 
                  color: '#2D2D2D',
                  fontWeight: 700,
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                }}
              >
                <PublicIcon sx={{ color: 'primary.main', fontSize: 40 }} />
                Our Locations
              </Typography>
              <Typography variant="body1" sx={{ color: '#616161', maxWidth: '600px', mx: 'auto' }}>
                Visit us at our international locations for personalized consultations
              </Typography>
            </Box>
            
            <Grid container spacing={4} justifyContent="center">
              {offices.map((office, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
                  >
                    <OfficeCard>
                      {/* Header with flag */}
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 2, 
                        mb: 3,
                        pb: 2,
                        borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      }}>
                        <Typography variant="h2" sx={{ fontSize: '3rem', lineHeight: 1 }}>
                          {office.flag}
                        </Typography>
                        <Box>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              color: 'primary.main',
                              fontWeight: 700,
                              letterSpacing: '0.05em',
                              fontSize: '1.1rem',
                              mb: 0.5
                            }}
                          >
                            {office.country}
                          </Typography>
                          <Chip 
                            label="Active" 
                            size="small"
                            sx={{ 
                              backgroundColor: alpha('#4CAF50', 0.1),
                              color: '#2E7D32',
                              fontSize: '0.6rem',
                              height: 20,
                              fontWeight: 600,
                            }}
                          />
                        </Box>
                      </Box>
                      
                      {/* Address */}
                      <Box sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
                        <LocationOnIcon sx={{ color: 'primary.main', fontSize: 20, mt: 0.3 }} />
                        <Box>
                          {office.address.map((line, i) => (
                            <Typography 
                              key={i} 
                              variant="body2" 
                              sx={{ 
                                color: '#616161',
                                lineHeight: 1.8,
                              }}
                            >
                              {line}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                      
                      {/* Phone */}
                      <Box sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
                        <PhoneIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                        <Typography variant="body2" sx={{ color: '#616161', fontWeight: 500 }}>
                          {office.phone}
                        </Typography>
                      </Box>
                      
                      {/* Email */}
                      <Box sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
                        <EmailIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                        <Typography 
                          variant="body2" 
                          component="a"
                          href={`mailto:${office.email}`}
                          sx={{ 
                            color: 'primary.main',
                            fontWeight: 600,
                            textDecoration: 'none',
                            '&:hover': {
                              textDecoration: 'underline',
                            }
                          }}
                        >
                          {office.email}
                        </Typography>
                      </Box>
                      
                      {/* Hours */}
                      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                        <AccessTimeIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                        <Typography variant="body2" sx={{ color: '#616161' }}>
                          {office.hours}
                        </Typography>
                      </Box>

                      <Divider sx={{ my: 2, borderColor: alpha('#C6A75E', 0.2) }} />
                      
                      {/* Local Time */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Chip 
                          label="Local Time"
                          size="small"
                          sx={{
                            backgroundColor: alpha('#C6A75E', 0.1),
                            color: '#616161',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                          }}
                        />
                        <Typography variant="caption" sx={{ color: alpha('#616161', 0.8), fontWeight: 500 }}>
                          {office.timezone}
                        </Typography>
                      </Box>

                      {/* Decorative corner accent */}
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          right: 0,
                          width: '60px',
                          height: '60px',
                          background: `linear-gradient(135deg, transparent 50%, ${alpha(theme.palette.primary.main, 0.1)} 50%)`,
                          pointerEvents: 'none',
                        }}
                      />
                    </OfficeCard>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Box>

        {/* FAQ Section */}
        <Box sx={{ mt: 10 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <Typography 
              variant="h4" 
              align="center" 
              gutterBottom 
              sx={{ 
                color: '#2D2D2D',
                fontWeight: 700,
                mb: 5,
                fontSize: { xs: '1.8rem', md: '2.2rem' }
              }}
            >
              Frequently Asked Questions
            </Typography>
            <Grid container spacing={3}>
              {[
                {
                  q: "How are coins authenticated?",
                  a: "All coins are verified by PCGS, NGC, or our in-house experts with documented provenance and certification."
                },
                {
                  q: "What payment methods do you accept?",
                  a: "We accept wire transfers, cryptocurrency, and verified escrow services for secure transactions."
                },
                {
                  q: "How is shipping handled?",
                  a: "All shipments are fully insured, discreet, and tracked with signature confirmation required upon delivery."
                },
                {
                  q: "Do you offer appraisals?",
                  a: "Yes, our experts provide confidential appraisals for insurance, estate planning, or sale purposes."
                },
              ].map((faq, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.7 + index * 0.1 }}
                  >
                    <Paper
                      sx={{
                        p: 3,
                        backgroundColor: '#FFFFFF',
                        border: '1px solid',
                        borderColor: alpha('#C6A75E', 0.15),
                        borderRadius: 2,
                        height: '100%',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: 'primary.main',
                          transform: 'translateY(-4px)',
                          boxShadow: `0 12px 32px ${alpha('#C6A75E', 0.12)}`,
                        },
                      }}
                    >
                      <Typography variant="subtitle1" gutterBottom sx={{ color: 'primary.main', fontWeight: 700, fontSize: '1rem', mb: 1.5 }}>
                        {faq.q}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#616161', lineHeight: 1.7, fontSize: '0.85rem' }}>
                        {faq.a}
                      </Typography>
                    </Paper>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Box>

        {/* Success Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            severity={snackbar.severity} 
            sx={{ 
              width: '100%',
              backgroundColor: alpha('#C6A75E', 0.1),
              color: '#2D2D2D',
              border: `1px solid ${alpha('#C6A75E', 0.3)}`,
              borderRadius: 2,
              '& .MuiAlert-icon': {
                color: 'primary.main',
              },
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default Contact;